import {
  Box,
  DataTableToolbarSkeleton,
  SkeletonGroup,
  WidgetGridSkeleton,
  type WidgetGridSkeletonTile,
  rowsNearestHeight,
  type SpacingToken,
} from "@salestrak/ui";

import { MANAGED_HOME_CONTENT_MAX_WIDTH } from "./surface-chrome";

/**
 * The row pitch every dashboard grid in the host renders at. 48px keeps
 * analytical cards dense while a widget's live `h` span still drives its
 * compact rendering; the skeleton shares it so the tiles land on the same
 * rows the cards will.
 */
export const DASHBOARD_ROW_HEIGHT = 48;
/** The board grid's gap — named once so the row math below and the grid
 *  itself can never disagree about a row's pitch. */
export const DASHBOARD_GRID_GAP = "md" satisfies SpacingToken;
/** Every widget opens about this tall — a new one from the gallery, a seeded
 *  one, a reset — and the user resizes from there. The rung just UNDER 600:
 *  Spencer chose 9 rows over the 624px the 10-row snap gave. */
export const DEFAULT_WIDGET_HEIGHT_PX = 560;
/** `DEFAULT_WIDGET_HEIGHT_PX` on this grid's snap points: the row count whose
 *  rendered height is nearest (9 rows = 560px at 48 + 16). A widget's span
 *  still clamps it (`defaultRowsForSpan`). */
export const DEFAULT_WIDGET_ROWS = rowsNearestHeight(
  DEFAULT_WIDGET_HEIGHT_PX,
  DASHBOARD_ROW_HEIGHT,
  DASHBOARD_GRID_GAP,
);

/**
 * The board silhouette every dashboard loads behind — one plausible board,
 * not THE board. The stored rows own the real geometry (any host, any layout
 * version, any fork), so no stand-in can promise a match; this one only
 * promises the rhythm the managed boards and most free boards share: a
 * full-width hero over two ranks of halves. Spans are 12-column storage
 * units, like `MY_WORK_BOARD`.
 */
const BOARD_SILHOUETTE: readonly WidgetGridSkeletonTile[] = [
  { w: 12, h: 4 },
  { w: 6, h: 5 },
  { w: 6, h: 5 },
  { w: 6, h: 4 },
  { w: 6, h: 4 },
];

/**
 * What the board area shows while `initialLoading` — the first load, an
 * uncached view switch or a transient working-copy reset. Delayed: a cached
 * view switch seeds synchronously and a warm query answers inside the ~200ms
 * window, so neither ever paints a tile; only a genuinely slow load does.
 * The chrome around it (masthead, toolbar, open popovers) stays mounted by
 * the host, so only the board flips.
 */
export function DashboardBoardSkeleton() {
  return (
    <WidgetGridSkeleton
      delayed
      tiles={BOARD_SILHOUETTE}
      rowHeight={DASHBOARD_ROW_HEIGHT}
      surface="elevated"
    />
  );
}

/**
 * The whole board pane as a stand-in, for a host that cannot mount
 * `Dashboard` yet (its view row or the current user is still unknown). It
 * mirrors the host's own board inset so the real pane lands without a
 * shift: `managed` is My Work's centered, capped column under its masthead;
 * the free variant leads with the toolbar band a free board opens with.
 * One delayed gate over the lot.
 */
export function DashboardBoardSkeletonPane({ managed = false }: { managed?: boolean }) {
  return (
    <SkeletonGroup className="flex min-h-0 flex-1 flex-col" data-slot="dashboard-board-skeleton-pane" aria-hidden>
      {managed ? null : <DataTableToolbarSkeleton />}
      <Box
        direction="column"
        align={managed ? "center" : undefined}
        paddingTop={managed ? "lg" : "md"}
        paddingX={{ base: "sm", md: managed ? "lg" : "md" }}
        paddingBottom="xl"
      >
        <Box direction="column" fill="width" maxWidth={managed ? MANAGED_HOME_CONTENT_MAX_WIDTH : undefined}>
          <WidgetGridSkeleton
            tiles={BOARD_SILHOUETTE}
            rowHeight={DASHBOARD_ROW_HEIGHT}
            surface="elevated"
          />
        </Box>
      </Box>
    </SkeletonGroup>
  );
}
